import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async ({ page = 0, limit = 20 } = {}) => {
    const res = await api.get("/notifications", { params: { page, limit } });
    return res.data;
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (arg, thunkAPI) => {
    const force = arg?.force === true;
    const state = thunkAPI.getState()?.notifications;
    const now = Date.now();
    if (!force && state?._lastFetchAt && now - state._lastFetchAt < 20000) {
      return state.unreadCount || 0;
    }
    const res = await api.get("/notifications/count");
    return res.data.count;
  }
);

export const markAllRead = createAsyncThunk(
  "notifications/markAllRead",
  async () => {
    const res = await api.post("/notifications/read-all");
    return res.data.updated;
  }
);

export const markRead = createAsyncThunk(
  "notifications/markRead",
  async (id) => {
    const res = await api.post(`/notifications/${id}/read`);
    return { id, updated: res.data.updated };
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState: { items: [], loading: false, error: null, page: 0, limit: 20, total: 0, hasMore: false, unreadCount: 0, _lastFetchAt: 0 },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const { notifications = [], page = 0, limit = state.limit, total = 0, hasMore = false } = action.payload || {};
        // Append on subsequent pages; replace on first page
        if (page > 0 && state.items && state.items.length) {
          const existingIds = new Set(state.items.map((n) => n.id));
          const merged = [...state.items, ...notifications.filter((n) => !existingIds.has(n.id))];
          state.items = merged;
        } else {
          state.items = notifications;
        }
        state.page = page;
        state.limit = limit;
        state.total = total;
        state.hasMore = hasMore;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Error fetching notifications";
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload || 0;
        state._lastFetchAt = Date.now();
      })
      .addCase(markAllRead.fulfilled, (state) => {
        state.unreadCount = 0;
        state.items = state.items.map((n) => ({ ...n, read: true }));
        state._lastFetchAt = Date.now();
      })
      .addCase(markRead.fulfilled, (state, action) => {
        const id = action.payload.id;
        const updated = Number(action.payload.updated) || 0;
        const wasUnread = state.items.some((n) => n.id === id && !n.read);
        state.items = state.items.map((n) => (n.id === id ? { ...n, read: true } : n));
        if (updated > 0 && wasUnread && state.unreadCount > 0) state.unreadCount -= 1;
        state._lastFetchAt = Date.now();
      });
  },
});

export default notificationsSlice.reducer;
